import React, {
  ReactElement,
  useMemo,
  useState,
  useCallback,
  useEffect
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import style from './app.module.css';
import ImproveContent from '../../components/improve-content/improve-content';
import Rate from '../../../../components/rate/rate';
import Toast from '../../../../components/toast';
import { saveFirstChargeQuestionInfo, validFirstChargeQuestionUid, getFirstChargeQuestionDetail } from '../../../../api/pile-apply';
import {
  closeWindow,
  getPlatform,
  go2LoginIfNot,
  webviewOpen
} from '../../../../utils/common';
import OrdinaryModel from '../../../../components/ordinary-model';
import Confirm from '../../../../components/confirm';
import FullLoading from '../../../../components/full-loading';
import FilterApp from '../../../../components/filter-app';

enum ServiceWeakness {
  unclearProcess,
  connectException,
  connectException4G,
  timingChargeException,
  lock,
  other
}

const App = (): ReactElement => {
  const router = useHistory();
  const loc = useLocation();
  const questionId: any = new URLSearchParams(loc.search).get('questionId');
  // 留言
  const [comment, setComment] = useState('');
  // 改进选项
  const improveArray = [
    { name: '使用教程不清晰', value: ServiceWeakness.unclearProcess },
    { name: '蓝牙连接异常', value: ServiceWeakness.connectException },
    { name: '4G连接异常', value: ServiceWeakness.connectException4G },
    { name: '定时充电异常', value: ServiceWeakness.timingChargeException },
    { name: '防盗锁', value: ServiceWeakness.lock },
    { name: '其他', value: ServiceWeakness.other }
  ];
  // 选中项
  const [selected, setSelected] = useState<number[]>([]);

  const [userInfo, setUserInfo] = useState({ uid: '', name: '' });

  const [rateVal, setRateVal] = useState(0);

  const [isSubmiting, setIsSubmiting] = useState(false);

  // 是否有权限
  const [isAuth, setIsAuth] = useState<boolean>(false);
  const [isCheckDetail, setIsCheckDetail] = useState<boolean>(false);

  useEffect(() => {
    document.title = '家充桩使用问卷';
  }, []);

  const getQuestionInfo = () => {
    getFirstChargeQuestionDetail({ questionId })
      .then(res => {
        if (res.data) {
          const { validCode, mark, dissatisfyOptions, info } = res.data;
          if (!validCode) {
            setIsAuth(false);
            // 提示问卷无权限
            Toast.show({
              text: '账号无权限',
            });
            return;
          } else {
            setIsAuth(true);
          }
          // 用一个必填字段判断该工单是否编辑过
          if(mark){
            setIsCheckDetail(true);
            setRateVal(mark);
            setSelected(dissatisfyOptions);
            setComment(info);
          }
        }
      })
      .catch(e => {
        console.log('获取问卷详情失败', e.message);
        closeWindow();
      });
  }

  const handleSelect = useCallback(
    (val: number[]) => {
      console.log('setSelected')
      if(isCheckDetail) return
      setSelected(val);
    },
    [selected]
  );

  const btnCanSubmit = useMemo(() => {
    if (!rateVal) return false;
    if (rateVal <= 4 && selected.length === 0) return false;
    if (selected && selected.includes(ServiceWeakness.other) && !comment) return false;
    return true;
  }, [rateVal, selected, comment]);

  const improveVisible = useMemo(() => {
    if (rateVal <= 4 && rateVal !== 0) return true;
    return false;
  }, [rateVal]);

  const handleChange = e => {
    const val = e.target.value;
    setComment(val);
  };

  const handleRate = val => {
    if (val > 3) {
      setSelected([]);
    }
    setRateVal(val);
  };

  const submit = () => {
    if (!btnCanSubmit) {
      Toast.show({
        text: '请完成表单',
        duration:2000
      });
      return;
    }
    setIsSubmiting(true);
    if (isSubmiting) {
      FullLoading.show({
        text:"提交中"
      })
      return;
    }
    saveFirstChargeQuestionInfo({
      questionId: questionId,
      mark: rateVal,
      dissatisfyOptions: selected,
      info: comment,
    })
      .then(() => {
        setIsSubmiting(false);
        setIsCheckDetail(true);
        console.log('提交成功');
        Toast.show({
          text: '提交成功',
          duration: 2000
        });
        FullLoading.hide()
      })
      .catch(e => {
        console.log('提交表单失败', e);
        Toast.show({
          text: e.message || '提交失败',
          duration: 2000
        });
        setIsSubmiting(false);
        FullLoading.hide()
      });
  };

  useEffect(() => {
    go2LoginIfNot()
      .then(loginData => {
        if (!loginData) {
          Confirm.open('请先登录', {
            type: 2,
            confirmText: '确定',
            header: '',
            confirmCbk: (destroy): void => {
              destroy();
              // 关闭h5
              closeWindow();
            }
          });
        } else {
          // 已登录
          const phoneModel = getPlatform();
          let name: string = loginData.username;
          if (phoneModel === 'android') {
            name = loginData.nickname;
          }
          setUserInfo({ uid: loginData.uid, name: name });
          getQuestionInfo();
        }
      })
      .catch(e => {
        console.log('页面出错', e.message);
        closeWindow();
      });
  }, []);
  return isAuth ? (
    <FilterApp>
      <div className={style.wrap}>
        <div className={style.content}>
          <p className={style.title}>你对家充桩使用体验的满意度</p>
          <Rate value={rateVal} onChange={handleRate} disabled={isCheckDetail}></Rate>
          {improveVisible && (
            <>
              <ImproveContent
                content={improveArray}
                selected={selected}
                handleSelect={handleSelect}
              ></ImproveContent>
            </>
          )}
          <p className={`${style.commentTitle} ${selected && selected.includes(ServiceWeakness.other) && style.required}`}>
            请问你在使用过程中遇到了什么问题？
          </p>
          <div className={style.inputWrap}>
            <textarea
              className={style.input}
              placeholder="请输入"
              onChange={handleChange}
              value={comment}
              disabled={isCheckDetail}
            ></textarea>
          </div>
        </div>

        {!isCheckDetail && <div className={style.bottom}>
          <button
            className={`${!btnCanSubmit ? '' : style.btn} ${style.disabledBtn}`}
            onClick={submit}
          >
            提交评价
          </button>
        </div>}
      </div>
    </FilterApp>
  ) : <></>;
};

export default App;
